<template>
  <div>
    <div class="document">
      <div class="header">
        <es-header>
          <template #header>
            <div class="header-title">
              <div>
                Search Results
              </div>
            </div>
          </template>
          <template #actions
            v-if="!loadingStudents
              && !loadingStudentsError
              && !isSearchByAdvanced
              && !show"
            >
            <div class="actions-container">
                <router-link
                  v-if="isSearchByLocation && hasModule('PRSID:DWNLD:DOWNLOAD') && studentsObject"
                  :to="{
                    name: 'pidSearchDownloadHeader',
                    params: {
                      active: searchParams.active,
                      districtCode: districtCode,
                      schoolCode: searchParams.schoolCode,
                      schoolYear: searchParams.schoolYear,
                      totalCount: totalCountResults,
                      personType: searchParams.personType
                    }
                  }"
                  class="button is-primary has-no-underline"
                  :class="{ disabled: !studentsObject || studentsObject.length === 0 }"
                  aria-label="Download Search Results"
                >
                  Download
                </router-link>
              <router-link
                v-if="hasModule('PRSID:MATCH:OPTIONS:PRSN:ADD')"
                :to="{
                  name: 'pidAddPerson',
                  params: {
                    searchParams: searchParams,
                    districtCode: districtCode
                  }
                }"
                class="button is-primary has-no-underline"
              >
                + Add New Person
              </router-link>
            </div>
          </template>
        </es-header>
      </div>
      <es-filter
        v-if="!loadingStudents
          && !loadingStudentsError
          && !isSearchByAdvanced
          && !show"
        :defaultExpanded="true"
      >
        <template #filter-options>
          <div class="columns is-3-desktop multi-dropdown-columns">
            <div v-if="isSearchByPerson && !contextMode" class="column is-2">
              <es-select
                api="person-types"
                keyValue="code"
                ref="selPersonTypeFilter"
                v-on:update:values="personTypeFilter = $event"
                :clear-out="true"
                :label="`Person Type`"
                :multi="true"
                :preSelected="personTypeFilter"
                :valueTplFn="(result) => `${result.name}`"
              />
            </div>
            <div class="column is-2">
              <es-select
                api="grades?all=true"
                keyValue="code"
                ref="selGrades"
                v-on:update:values="grades = $event"
                :clear-out="true"
                :label="`${getFieldLabel(`PER:${personTypeForLabels}:SEARCH:GRADE_CODE`)} :`"
                :multi="true"
                :preSelected="grades"
                :valueTplFn="(result) => `${result.description}`"
              />
            </div>
            <div v-if="!isSearchByLocation" class="column is-2">
              <es-select
                api="districts"
                keyValue="code"
                ref="selDistricts"
                searchMinLength="3"
                v-on:update:values="districts = $event"
                :label="`${getFieldLabel(`PER:${personTypeForLabels}:SEARCH:DISTRICT_CODE`)} :`"
                :multi="true"
                :preSelected="districts"
                :search="true"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
              />
            </div>
            <div v-if="!isSearchByLocation" class="column is-2">
              <es-select
                api="schools"
                keyValue="code"
                ref="selSchools"
                searchMinLength="3"
                v-on:update:values="schools = $event"
                :apiQueryParams="getDistrictParams"
                :label="`${getFieldLabel(`PER:${personTypeForLabels}:SEARCH:SCHOOL_CODE`)} :`"
                :multi="true"
                :preSelected="schools"
                :search="true"
                :subValueTplFn="(result) =>
                  `${result.districtLocation.name} (<i>${result.districtCode}</i>)`"
                :valueTplFn="(result) => `${result.location.name} (${result.code})`"
              />
            </div>
          </div>
          <div style="padding: 1rem">
            <div class="field is-grouped flex-justify--end">
              <div class="control is-flex flex-align--center">
                <a @click.prevent="resetFilter"
                  @keypress="resetFilter"
                  tabindex="0" role="link"
                >
                  Reset
                </a>
              </div>
              <div class="control">
                <button
                  class="button is-primary"
                  @click="filterData"
                >
                  Filter Data
                </button>
              </div>
            </div>
          </div>
        </template>
      </es-filter>
      <div v-if="isSearchByAdvanced && !show && this.studentsObject.length">
        <es-profile-header style="margin-bottom: 2rem">
          <template #profile-header>
            <div class="columns">
              <div class="column">
                <div class="column-content">
                  <div class="header-title has-text-weight-bold is-size-5">
                    Advanced Search limits results to most recent
                    {{ getAdvancedSearchLimit }} records
                  </div>
                </div>
              </div>
            </div>
          </template>
          <template #profile-overview>
            <div class="columns">
              <div class="column">
                <div class="column-content">
                  <div class="column-title">
                    {{ advancedSearchLimit }}
                  </div>
                  <div class="column-subtitle">
                    Results Return Count
                  </div>
                </div>
              </div>
              <div class="column">
                <div class="column-content">
                  <div class="column-title">
                    {{ totalAdvancedResultsCount }}
                  </div>
                  <div class="column-subtitle">
                    Total Results Count
                  </div>
                </div>
              </div>
            </div>
          </template>
        </es-profile-header>
      </div>
      <div class="table-wrapper">
        <es-loader
          v-if="loadingStudents || loadingSearchOptions"
          style="margin: 6.5rem"
        />
        <es-exception
          v-else-if="show"
          style="padding: 6.5rem"
          margin="0rem"
          size="4x"
          exceptionMessage="Search criteria must be entered to view results."
        />
        <es-exception
          v-else-if="loadingStudentsError"
          style="padding: 6.5rem"
          margin="0rem"
          size="4x"
          exceptionMessage="There was an issue. Please contact the admin"
        />
        <es-exception
          v-else-if="!show
            && !this.studentsObject.length
            && !loadingStudents
            && !loadingStudentsError
            && !loadingSearchOptions"
          style="padding: 6.5rem"
          margin="0rem"
          size="4x"
          exceptionMessage="No results were found."
        />
        <table v-else class="table is-fullwidth is-hoverable">
          <es-sorter
            :columnHeaders="filteredColumnHeaders"
            @updateSorter="updateSorter"
          />
          <tbody>
            <tr
              v-for="(student, index) in studentsObject"
              :key="index"
            >
              <td v-if="isSearchByPerson">
                <es-eligibility-marker
                  size="small"
                  :value="student.matchScore"
                  :isPercent="true"
                  :upperBound="upperBound"
                  :lowerBound="lowerBound"
                  title="Match"
                  fieldTitle="Match Score" />
              </td>
              <td>
                <div class="has-text-weight-bold">
                  <span>
                    {{ formatStateID(student.stateStudentId) }}
                  </span>
                </div>
                <div
                  v-if="student.retired"
                  style="color: #D10000"
                >
                  (Retired ID)
                </div>
                <div v-if="student.associatedId">
                  {{ formatStateID(student.associatedId) }}<br>
                  (Associated Active ID)
                </div>
              </td>
              <td v-if="hasModule('PRSID:PRPRF:PROFILE:VIEW') && student.stateStudentId !== '--'">
                <router-link
                  :to="{
                    path: personProfileDrillIn(student.stateStudentId),
                    query: {
                      retired: student.retired,
                    },
                  }"
                >
                  {{ student.name }}
                </router-link>
              </td>
              <td v-else>
                {{ student.name }}
              </td>
              <td class="has-text-weight-bold">
                {{ displayDOBFormat(student.dateOfBirth) }}
              </td>
              <td v-if="fieldEnabled('GENDER_CODE')" >
                {{ getDescriptionFromCode(student.gender, activeGenders) }}
              </td>
              <td v-if="hasAnyRaceField">
                <div v-if="fieldEnabled('ETHNICITY_INDICATOR')">
                  {{ getDescriptionFromCode(student.ethnicity, activeEthnicities, '--') }}
                </div>
                <span>
                  {{ getStudentRaceValues(student) }}
                </span>
              </td>
              <td v-if="fieldEnabled('SOCIAL_SECURITY_NUMBER')">
                <div v-if="hasModule('PRSID:PRPRF:SSN:VIEW')">
                  {{ student.socialSecurityNumber }}
                </div>
              </td>
              <td v-if="fieldEnabled('GRADE_CODE')" class="text-wrap">
                <span v-if="student.grade">
                  {{ getDescriptionFromCode(student.grade, activeGrades) }}
                </span>
              </td>
              <td
                v-if="fieldEnabled('DISTRICT_CODE')
                  || fieldEnabled('SCHOOL_CODE')"
              >
                <div class="is-flex flex-direction--column">
                  <div v-if="fieldEnabled('DISTRICT_CODE')" >
                    <div v-if="isMultiDistrict">
                      <b style="font-size:12px">
                        Multiple Locations
                      </b>
                    </div>
                    <div v-else>
                      <router-link
                        :to="{
                          name: 'PIDistrictInfoDetails',
                          params: { code: student.districtCode },
                        }"
                        class="has-text-weight-bold"
                      >
                        {{ student.districtName }}
                      </router-link>
                      <span class="has-text-grey is-italic">
                        ({{ student.districtCode }})
                      </span>
                    </div>
                  </div>
                  <div v-if="fieldEnabled('SCHOOL_CODE')">
                    <div v-if="isMultiSchool">
                      <span class="is-italic has-text-grey">
                        Multiple Buildings
                      </span>
                    </div>
                    <div v-else>
                      <router-link
                        :to="{
                          name: 'PISchoolInfoDetails',
                          params: {
                            districtCode: student.districtCode,
                            schoolCode: student.schoolCode,
                          },
                        }"
                        class="is-italic"
                      >
                        {{ student.schoolName }}
                      </router-link>
                      <span class="has-text-grey is-italic">
                        ({{ student.schoolCode }})
                      </span>
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div>
                  <div>
                    <span v-if="fieldEnabled('LOCAL_STUDENT_ID')" class="has-text
                     z-weight-bold">
                      {{ student.localId }}
                    </span>
                    <span v-else>
                      --
                    </span>
                  </div>
                  <div class="has-text-grey">
                    {{ student.idInfo }}
                  </div>
                </div>
              </td>
              <td v-if="student.personType && !contextMode">
                  {{ getNameFromCode(student.personType, activePersonTypes) }}
              </td>
              <td v-if="student.matchScore">
                <vc-donut
                  v-if="isSearchByPerson && student.matchScore === 100"
                  :size="60"
                  :sections="[
                  { value: student.matchScore, color: '#215E97' }]"
                >
                  <h1 class="percent-label">{{ student.matchScore }}%</h1>
                </vc-donut>
                <vc-donut
                  v-else-if="isSearchByPerson"
                  :size="60"
                  :sections="[
                  { value: student.matchScore - 1, color: '#215E97' },
                  { value: 1, color: '#FFFFFF' },
                  { value: 99 - student.matchScore, color: '#C8C8C8'},
                  { value: 1, color: '#FFFFFF' },]"
                >
                  <h1 class="percent-label">{{ student.matchScore }}%</h1>
                </vc-donut>
                <span v-else>
                  {{ student.matchScore }}
                </span>
              </td>
              <td v-else-if="isSearchByAdvanced">
                {{ student.lastUpdateDate | formatDate }}
              </td>
              <td v-else>
                N/A
              </td>
              <td
                v-if="hasModule('PRSID:PRPRF:PROFILE:VIEW')"
                style="padding-left: 30px"
              >
                <router-link
                :to="searchParams.selectedSearchInOption === 'Submission'
                  ? {
                      path: transactionProfileDrillIn(student.id),
                    }
                  : {
                    path: personProfileDrillIn(student.stateStudentId),
                    query: {
                      retired: student.retired,
                    },
                  }"
                  :class="{ disabled: searchParams.selectedSearchInOption
                    !== 'Submission' && student.stateStudentId === '--' }"
                >
                  <font-awesome-icon
                    :icon="['far', 'angle-double-right']"
                    class="fa-2x"
                    title="View Details"
                  />
                </router-link>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="pager-container" v-if="!loadingStudents && totalCountResults">
        <es-pager :totalCount="totalCountResults"/>
      </div>
      <legend-near-match
        v-if="!loadingStudents
          && !loadingStudentsError
          && isSearchByPerson
          && studentsObject
          && studentsObject.length"
        :showDecisions="false"
      />
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import axios from 'axios';
import qs from 'qs';
import { mapGetters, mapActions } from 'vuex';
import Donut from 'vue-css-donut-chart';
import 'vue-css-donut-chart/dist/vcdonut.css';
import EsException from '@/components/EsException.vue';
import EsEligibilityMarker from '@/components/EsEligibilityMarker.vue';
import EsFilter from '@/components/EsFilter.vue';
import EsPager from '@/components/EsPager.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import EsSorter from '@/components/EsSorter.vue';
import PermissionManager from '@/mixins/PermissionManager';
import EsProfileHeader from '@/components/EsProfileHeader.vue';
import LegendNearMatch from '@/components/LegendNearMatch.vue';
import moment from 'moment';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faFileDownload,
} from '@fortawesome/pro-regular-svg-icons';
import FieldLabels from '@/mixins/FieldLabels';
import DateUtils from '@/mixins/DateUtils';
import StateIdUtils from '@/mixins/StateIdUtils';
import LookupsUtils from '@/mixins/LookupsUtils';

library.add(
  faAngleDoubleRight,
  faFileDownload,
);

Vue.use(Donut);

export default {
  mixins: [
    DateUtils,
    LookupsUtils,
    StateIdUtils,
    FieldLabels,
    PermissionManager,
  ],
  components: {
    EsException,
    EsEligibilityMarker,
    EsFilter,
    EsHeader,
    EsProfileHeader,
    EsLoader,
    EsPager,
    EsSelect,
    EsSorter,
    LegendNearMatch,
  },
  props: {
    districtCode: {
      type: String,
    },
    page: {
      type: Number,
    },
    sortField: {
      type: String,
    },
    sortOrder: {
      type: String,
    },
    show: {
      type: Boolean,
      default: true,
    },
    loadingSearchOptions: {
      type: Boolean,
      default: false,
    },
    students: {
      type: Array,
    },
    searchParams: {
      type: Object,
    },
    totalCount: {
      type: Number,
    },
  },
  data() {
    return {
      active: null,
      columnHeaders: [],
      districts: [],
      ethnicities: [],
      genders: [],
      grades: [],
      loadingStudents: false,
      loadingStudentsError: false,
      locationNames: [],
      personType: null,
      personTypeFilter: [],
      personLocations: [],
      races: [],
      results: {},
      schools: [],
      schoolCode: null,
      schoolYear: null,
      selectedMatchMethod: null,
      selectedSearchInOption: null,
      studentsObject: [],
      studentInfoCleaned: [],
      totalCountResults: this.totalCount,
      advancedSearchLimit: null,
      totalAdvancedResultsCount: null,
      submissionTypeCode: null,
      userPersonTypesCodes: null,
      upperBound: null,
      lowerBound: null,
      lastSearchSignature: null,
    };
  },
  async created() {
    await this.loadPersonTypes();
    this.setDataElements();
    this.studentsObject = this.students;
    this.lowerBound = Number(this.properties['ELASTIC_LOCATION-LOWER_BOUND_FOR_NEAR_MATCHES']);
    this.upperBound = Number(this.properties['ELASTIC_LOCATION-UPPER_BOUND_FOR_NEAR_MATCHES']);
  },
  watch: {
    students() {
      this.studentsObject = this.students;
    },
    show() {
      if (this.show === true) {
        this.studentsObject = [];
        this.clearFilters();
        this.lastSearchSignature = null;
      } else {
        this.runSearchFromCurrentState();
      }
    },
    searchParams: {
      deep: true,
      handler() {
        this.runSearchFromCurrentState();
      },
    },
    $route() {
      this.runSearchFromCurrentState();
    },
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('lookups', [
      'activeEthnicities',
      'activeGenders',
      'activeGrades',
      'activeRaces',
      'activePersonTypes',
      'defaultPersonType',
    ]),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldEnabledForPersonType',
    ]),
    getAdvancedSearchLimit() {
      return this.properties['RULE-RESULTS_LIMIT_ADVSRCH'];
    },
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
    currentPersonTypeCode() {
      const personTypeCodeList = this.$route.query.personTypeCodeList || [];

      if (personTypeCodeList.length === 1) {
        return this.userPersonTypes.find(r => r.code === personTypeCodeList[0]).code;
      }

      if (this.$route.query.personType) {
        return this.userPersonTypes.find(r => r.code === this.$route.query.personType).code;
      }
      return this.userPersonTypes.find(r => r.code === (
        this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0]
      )).code;
    },
    filteredColumnHeaders() {
      return this.columnHeaders.filter(item => item.show);
    },
    filterSortParams() {
      const params = {};
      // Params for Search by ID
      // if (this.$route.query.idType) {
      //   params.idType = this.$route.query.idType;
      // }
      // if (this.$route.query.id) {
      //   params.id = this.$route.query.id;
      // }
      if (this.$route.query.sourceCode) {
        params.sourceCode = this.$route.query.sourceCode;
      }

      // Params for Search by Location
      if (this.distictCode) {
        params.district = this.districtCode;
      }
      if (this.searchParams.schoolCode) {
        params.school = this.searchParams.schoolCode;
      }
      if (this.searchParams.active) {
        params.active = this.searchParams.active;
      }
      if (this.searchParams.schoolYear) {
        params.schoolYear = this.searchParams.schoolYear;
      }

      // Params for Search by Person
      if (this.searchParams.selectedMatchMethod) {
        params.matchMethod = this.searchParams.selectedMatchMethod;
      }
      if (this.searchParams.selectedSearchInOption) {
        params.searchType = this.searchParams.selectedSearchInOption;
      }
      // if (this.searchParams.firstName) {
      //   params.firstName = this.searchParams.firstName;
      // }
      // if (this.searchParams.middleName) {
      //   params.middleName = this.searchParams.middleName;
      // }
      // if (this.searchParams.lastName) {
      //   params.lastName = this.searchParams.lastName;
      // }
      if (this.searchParams.suffix) {
        params.suffix = this.searchParams.suffix;
      }
      // if (this.searchParams.dateOfBirth) {
      //   params.dob = this.searchParams.dateOfBirth;
      // }

      // Common Sorter Params
      if (this.getSorter) {
        params.sortField = this.getSorter.field;
        params.sortOrder = this.getSorter.order;
      }
      if (this.searchParams.personType) {
        params.personType = this.searchParams.personType;
      }
      if (this.grades) {
        params.grade = this.grades.map(g => g.code);
      }
      if (this.districts) {
        params.districts = this.districts.map(g => g.code);
      }
      if (this.schools) {
        params.schools = this.schools.map(g => g.code);
      }
      if (this.personTypeFilter) {
        params.personTypeFilter = this.personTypeFilter.map(g => g.code);
      }
      return params;
    },
    getDistrictParams() {
      const params = {};
      params.districtCode = this.districts.map(d => d.code);
      return params;
    },
    getSorter() {
      return this.columnHeaders.find(header => header.order);
    },
    hasInputParams() {
      return this.grades.length || this.districts.length
        || this.schools.length || this.personTypeFilter.length;
    },
    isMultiDistrict() {
      const setOfUniqueDistricts = this.personLocations
        .map(a => a.districtCode)
        .slice().sort((a, b) => a > b)
        .reduce((a, b) => { if (a.slice(-1)[0] !== b) a.push(b); return a; }, []);
      if (setOfUniqueDistricts.length > 1) return true;
      return false;
    },
    isMultiSchool() {
      const setOfUniqueSchools = this.personLocations.map(a => a.schoolCode)
        .slice().sort((a, b) => a > b)
        .reduce((a, b) => { if (a.slice(-1)[0] !== b) a.push(b); return a; }, []);
      if (setOfUniqueSchools.length > 1) return true;
      return false;
    },
    isSearchByLocation() {
      if (this.$route.name === 'PidSearchLocation') {
        return true;
      }
      return false;
    },
    isSearchById() {
      if (this.$route.name === 'PidSearchId') {
        return true;
      }
      return false;
    },
    isSearchByPerson() {
      if (this.$route.name === 'PidSearchPerson') {
        return true;
      }
      return false;
    },
    isSearchByAdvanced() {
      if (this.$route.name === 'PidSearchAdvanced') {
        return true;
      }
      return false;
    },
    locationInfo() {
      if (this.studentsObject.find(({ stateStudentId }) => stateStudentId)) {
        return this.studentsObject.map(s => s.stateStudentId);
      }
      return this.studentsObject.map(p => p.personId);
    },
    personSearchParams() {
      const params = {};
      params.firstName = this.searchParams.firstName;
      params.middleName = this.searchParams.middleName;
      params.lastName = this.searchParams.lastName;
      params.suffix = this.searchParams.suffix;
      params.dateOfBirth = this.searchParams.dateOfBirth;
      params.personType = this.searchParams.personType;

      params.gender = this.searchParams.gender;
      // params.ssn = this.searchParams.ssn;
      if (this.searchParams.selectedSearchInOption !== 'Master') {
        params.schoolYear = this.searchParams.schoolYearInput;
      }
      params.grades = this.grades.length ? this.grades.map(g => g.code) : undefined;
      params.districts = this.districts.length ? this.districts.map(d => d.code) : undefined;
      params.schools = this.schools.length ? this.schools.map(s => s.code) : undefined;
      params.personTypeFilter = this.personTypeFilter.length
        ? this.personTypeFilter.map(s => s.code) : undefined;
      params.pageNum = this.page ? this.page : undefined;
      // params.sort = JSON.stringify([{
      //   field: this.sortField,
      //   order: this.sortOrder,
      // }]);
      return params;
    },
    personTypeForLabels() {
      return this.searchParams.personType ? this.searchParams.personType : this.defaultPersonType;
    },
    idParams() {
      const params = {};
      params.id = this.searchParams.id ? this.searchParams.id : undefined;
      params.personType = this.searchParams.personType ? this.searchParams.personType : undefined;
      params.idType = this.searchParams.idType ? this.searchParams.idType : undefined;
      params.sourceCode = this.searchParams.sourceCode ? this.searchParams.sourceCode : undefined;
      params.grades = this.grades.length ? this.grades.map(g => g.code) : undefined;
      params.districts = this.districts.length ? this.districts.map(d => d.code) : undefined;
      params.schools = this.schools.length ? this.schools.map(s => s.code) : undefined;
      params.pageNum = this.page ? this.page : undefined;
      // params.sort = JSON.stringify([{
      //   field: this.sortField,
      //   order: this.sortOrder,
      // }]);
      return params;
    },
    locationParams() {
      const params = {};
      params.schoolCode = this.searchParams.schoolCode !== null
        ? this.searchParams.schoolCode : undefined;
      params.active = this.searchParams.active;
      params.schoolYear = this.searchParams.schoolYear;
      params.personType = this.searchParams.personType;
      params.pageNum = this.page ? this.page : undefined;
      params.grades = this.grades.length ? this.grades.map(g => g.code) : undefined;
      params.sort = JSON.stringify([{
        field: this.sortField,
        order: this.sortOrder,
      }]);
      return params;
    },
    advancedSearchParams() {
      const params = {
        personTypeCodeList: this.searchParams.personTypes
          ? this.searchParams.personTypes : undefined,
        searchIn: this.searchParams.selectedSearchInOption,
        specVersion: '3.0',
        transmissionIdentifier: 'N/A',
        advancedSearchObject: [
          {
            advancedSQL: this.searchParams.sqlQuery,
          },
        ],
        pageNumber: this.page ? this.page : undefined,
        sort: JSON.stringify([
          {
            field: this.sortField,
            order: this.sortOrder,
          },
        ]),
      };
      return params;
    },
    enabledRaceFields() {
      const raceFieldMappings = [
        { code: 'RACE_ETHNIC_CODE', prop: 'race' },
        { code: 'RACE2_CODE', prop: 'race2' },
        { code: 'RACE3_CODE', prop: 'race3' },
        { code: 'RACE4_CODE', prop: 'race4' },
        { code: 'RACE5_CODE', prop: 'race5' },
      ];
      return raceFieldMappings.filter(field => this.fieldEnabled(field.code));
    },
    hasAnyRaceField() {
      return this.fieldEnabled('ETHNICITY_INDICATOR') || this.enabledRaceFields.length > 0;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadPersonTypes',
    ]),
    clearFilters() {
      if (this.districts.length && this.$refs.selDistricts) {
        this.$refs.selDistricts.clear();
      }
      if (this.schools.length && this.$refs.selSchools) {
        this.$refs.selSchools.clear();
      }
      if (this.$refs.selPersonTypeFilter) {
        this.$refs.selPersonTypeFilter.clear();
      }
      if (this.$refs.selGrades) {
        this.$refs.selGrades.clear();
      }
    },
    hasNonEmptyValue(value) {
      return value !== null
        && value !== undefined
        && `${value}`.trim() !== '';
    },
    hasLocationSearchCriteria() {
      return this.hasNonEmptyValue(this.districtCode);
    },
    hasPersonSearchCriteria() {
      return this.hasNonEmptyValue(this.searchParams.firstName);
    },
    hasIdSearchCriteria() {
      return this.hasNonEmptyValue(this.searchParams.id);
    },
    hasAdvancedSearchCriteria() {
      return this.hasNonEmptyValue(this.searchParams.sqlQuery);
    },
    searchSignature() {
      return JSON.stringify({
        routeName: this.$route.name,
        routePath: this.$route.fullPath,
        districtCode: this.districtCode,
        searchParams: this.searchParams,
      });
    },
    runSearchFromCurrentState() {
      if (this.show) {
        return;
      }

      let canSearch = false;
      let searchFn = null;

      if (this.isSearchByLocation && this.hasLocationSearchCriteria()) {
        canSearch = true;
        searchFn = this.doLocationSearch;
      } else if (this.isSearchByPerson && this.hasPersonSearchCriteria()) {
        canSearch = true;
        searchFn = this.doPersonSearch;
      } else if (this.isSearchById && this.hasIdSearchCriteria()) {
        canSearch = true;
        searchFn = this.doIdSearch;
      } else if (this.isSearchByAdvanced && this.hasAdvancedSearchCriteria()) {
        canSearch = true;
        searchFn = this.doAdvancedSearch;
      }

      if (!canSearch) {
        return;
      }

      const signature = this.searchSignature();
      if (signature === this.lastSearchSignature) {
        return;
      }

      this.lastSearchSignature = signature;
      searchFn();
      this.setDataElements();
    },
    async doLocationSearch() {
      try {
        this.loadingStudents = true;
        this.loadingStudentsError = false;
        this.$emit('loadingStudents', true);
        const results = await axios.get(`person-search/by-location/${this.districtCode}`,
          {
            params: this.locationParams,
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
        this.prepForSearchResultsDisplay(results.data.data);
        this.totalCountResults = results.data.totalCount;
      } catch (e) {
        this.loadingStudentsError = true;
      } finally {
        this.loadingStudents = false;
        this.$emit('loadingStudents', false);
      }
    },
    async doIdSearch() {
      try {
        this.loadingStudents = true;
        this.loadingStudentsError = false;
        this.$emit('loadingStudents', true);
        const results = await axios.post('person-search/by-id', this.idParams, {
          params: {
            sort: JSON.stringify([
              {
                field: this.sortField,
                order: this.sortOrder,
              },
            ]),
          },
          paramsSerializer(params) {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        });
        this.prepForSearchResultsDisplay(results.data.data);
        this.totalCountResults = results.data.totalCount;
      } catch (e) {
        this.loadingStudentsError = true;
      } finally {
        this.loadingStudents = false;
        this.$emit('loadingStudents', false);
      }
    },
    async doPersonSearch() {
      try {
        if (this.searchParams.selectedMatchMethod === 'Matching Engine') {
          this.loadingStudents = true;
          this.loadingStudentsError = false;
          this.$emit('loadingStudents', true);
          const results = await axios.post('person-search/by-person/matching-engine', this.personSearchParams, {
            params: {
              sort: JSON.stringify([
                {
                  field: this.sortField,
                  order: this.sortOrder,
                },
              ]),
            },
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
          this.prepForSearchResultsDisplay(results.data.potentialMatches.map(p => p));
          this.totalCountResults = results.data.totalCount;
        }
        if (this.searchParams.selectedMatchMethod === 'SQL') {
          if (this.searchParams.selectedSearchInOption === 'Submission') {
            this.searchParams.selectedSearchInOption = 'Stage';
          }
          this.loadingStudents = true;
          this.loadingStudentsError = false;
          this.$emit('loadingStudents', true);
          const results = await axios.post(`person-search/by-person/${this.searchParams.selectedSearchInOption}`, this.personSearchParams, {
            params: {
              sort: JSON.stringify([
                {
                  field: this.sortField,
                  order: this.sortOrder,
                },
              ]),
            },
            paramsSerializer(params) {
              return qs.stringify(params, { arrayFormat: 'repeat' });
            },
          });
          this.prepForSearchResultsDisplay(results.data.data);
          this.totalCountResults = results.data.totalCount;
        }
      } catch (e) {
        this.loadingStudentsError = true;
      } finally {
        this.loadingStudents = false;
        this.$emit('loadingStudents', false);
      }
    },
    async doAdvancedSearch() {
      try {
        this.loadingStudents = true;
        this.loadingStudentsError = false;
        this.$emit('loadingStudents', true);
        const results = await axios.post('person-search/advanced-sync', this.advancedSearchParams);
        let persons = [];
        const batchList = results.data.batchList || [];
        if (batchList.length > 0 && batchList[0].pidMultiSearchRecordResults) {
          const recordResults = batchList[0].pidMultiSearchRecordResults;

          switch (this.searchParams.selectedSearchInOption) {
            case 'Submission':
              persons = this.prepForSearchResultsDisplay(
                recordResults.map(p => p.pidSubmissionList || []).flat(),
              );
              break;
            case 'History':
              persons = this.prepForSearchResultsDisplay(
                recordResults.map(p => p.personHistoryList || []).flat(),
              );
              break;
            case 'Master':
              persons = this.prepForSearchResultsDisplay(
                recordResults.map(p => p.persons || []).flat(),
              );
              break;
            default:
              break;
          }

          this.totalAdvancedResultsCount = recordResults[0].totalCount || persons.length;
          this.advancedSearchLimit = Math
            .min(this.totalAdvancedResultsCount, this.getAdvancedSearchLimit);
          this.totalCountResults = this.advancedSearchLimit;

          this.submissionTypeCode = (batchList[0].pidMultiSearch
            && batchList[0].pidMultiSearch.submissionTypeCode) || null;
        }
      } catch (e) {
        this.loadingStudentsError = true;
      } finally {
        this.loadingStudents = false;
        this.$emit('loadingStudents', false);
      }
    },
    fieldEnabled(fieldLabel) {
      return this.getFieldEnabledForPersonType(fieldLabel, 'SEARCH', this.currentPersonTypeCode);
    },
    flatten(obj) {
      const flattenedObject = {};
      this.traverseAndFlatten(obj, flattenedObject);
      return flattenedObject;
    },
    filterData() {
      this.$router.replace({ query: this.filterSortParams });
    },
    getNameFromCode(code, list) {
      if (code) {
        const find = list.find(e => e.code === code);
        return find ? find.name : code;
      }
      return '--';
    },
    getStudentRaceValues(student) {
      const raceValues = this.enabledRaceFields
        .map(field => this.getDescriptionFromCode(
          student[field.prop], this.activeRaces, null,
        )).filter(val => val);
      return raceValues.length > 0 ? raceValues.join(', ') : '--';
    },
    personProfileDrillIn(student) {
      return `../personProfile/${student}/overview`;
    },
    transactionProfileDrillIn(student) {
      return `../individualMatch/${student}/person`;
    },

    prepForSearchResultsDisplay(array) {
      if (this.searchParams.selectedMatchMethod === 'Matching Engine') {
        this.studentInfoCleaned = array.map(p => ({
          stateStudentId: p.id,
          name: `${p.matchedObject.lastName}, ${p.matchedObject.firstName}`,
          dateOfBirth: p.matchedObject.dateOfBirth,
          gender: p.matchedObject.genderCode,
          ethnicity: p.matchedObject.ethnicityIndicator,
          race: p.matchedObject.raceEthnicCode,
          race2: p.matchedObject.race2Code,
          race3: p.matchedObject.race3Code,
          race4: p.matchedObject.race4Code,
          race5: p.matchedObject.race5Code,
          socialSecurityNumber: p.matchedObject.socialSecurityNumber,
          grade: p.matchedObject.gradeCode,
          districtCode: p.matchedObject.districtCode,
          districtName: p.matchedObject.districtName,
          schoolCode: p.matchedObject.schoolCode,
          schoolName: p.matchedObject.schoolName,
          localId: `${p.matchedObject.localId ? p.matchedObject.localId : '--'}`,
          idInfo: p.matchedObject.sourceCode,
          personType: p.matchedObject.personType,
          matchScore: p.percent,
        }));
      } if (this.searchParams.selectedMatchMethod === 'SQL') {
        this.studentInfoCleaned = array.map(p => ({
          // eslint-disable-next-line no-nested-ternary
          stateStudentId: `${p.stateStudentId ? p.stateStudentId : (p.personId ? p.personId : '--')}`,
          name: `${p.lastName}, ${p.firstName}`,
          dateOfBirth: p.dateOfBirth,
          gender: p.genderCode,
          ethnicity: p.ethnicityIndicator,
          race: p.raceEthnicCode,
          race2: p.race2Code,
          race3: p.race3Code,
          race4: p.race4Code,
          race5: p.race5Code,
          socialSecurityNumber: p.socialSecurityNumber,
          grade: p.gradeCode,
          districtCode: p.districtCode,
          districtName: `${p.district.location.name ? p.district.location.name : '--'}`,
          schoolCode: p.schoolCode,
          schoolName: `${p.school.location.name ? p.school.location.name : '--'}`,
          // eslint-disable-next-line no-nested-ternary
          localId: `${p.localId ? p.localId : (p.localStudentId ? p.localStudentId : '--')}`,
          idInfo: p.sourceCode,
          personType: p.personType,
          matchScore: p.percent,
        }));
      } if (this.isSearchById) {
        this.studentInfoCleaned = array.map(p => ({
          stateStudentId: p.stateStudentId,
          name: `${p.lastName}, ${p.firstName}`,
          dateOfBirth: p.dateOfBirth,
          gender: p.genderCode,
          ethnicity: p.ethnicityIndicator,
          race: p.raceEthnicCode,
          race2: p.race2Code,
          race3: p.race3Code,
          race4: p.race4Code,
          race5: p.race5Code,
          socialSecurityNumber: p.socialSecurityNumber,
          grade: p.gradeCode,
          districtCode: p.districtCode,
          districtName: `${p.district.location.name ? p.district.location.name : '--'}`,
          districtStateId: `${p.district.stateLocationId ? p.district.stateLocationId : '--'}`,
          schoolCode: p.schoolCode,
          schoolName: `${p.school.location.name ? p.school.location.name : '--'}`,
          schoolStateId: `${p.school.stateLocationId ? p.school.stateLocationId : '--'}`,
          localId: `${p.localId ? p.localId : '--'}`,
          idInfo: p.sourceCode,
          matchScore: p.pidMatchTransId_matchPercent,
          retired: p.retired,
          ...(p.retiredReference && { associatedId: p.retiredReference.idMerged }),
        }));
      } if (this.isSearchByLocation) {
        this.studentInfoRaw = [];
        array.forEach((item) => {
          const each = this.flatten(item);
          this.studentInfoRaw.push(each);
        });
        this.studentInfoCleaned = this.studentInfoRaw.map(p => ({
          stateStudentId: p.personId,
          name: `${p.student_lastName}, ${p.student_firstName}`,
          dateOfBirth: p.student_dateOfBirth,
          gender: p.student_genderCode,
          ethnicity: p.student_ethnicityIndicator,
          race: p.student_raceEthnicCode,
          race2: p.student_race2Code,
          race3: p.student_race3Code,
          race4: p.student_race4Code,
          race5: p.student_race5Code,
          socialSecurityNumber: p.student_socialSecurityNumber,
          grade: p.gradeCode,
          districtCode: p.districtCode,
          districtName: p.districtName,
          schoolCode: p.schoolCode,
          schoolName: p.schoolName,
          localId: `${p.localId ? p.localId : '--'}`,
          idInfo: p.sourceCode,
          matchScore: p.pidMatchTransId_matchPercent,
        }));
      }
      if (this.isSearchByAdvanced) {
        this.studentInfoCleaned = array.map(p => ({
          stateStudentId: `${p.stateStudentId || p.personId || '--'}`,
          name: `${p.lastName}, ${p.firstName}`,
          dateOfBirth: p.dateOfBirth,
          gender: p.genderCode,
          ethnicity: p.ethnicityIndicator,
          race: p.raceEthnicCode,
          race2: p.race2Code,
          race3: p.race3Code,
          race4: p.race4Code,
          race5: p.race5Code,
          socialSecurityNumber: p.socialSecurityNumber,
          grade: p.gradeCode,
          districtCode: p.districtCode,
          districtName: `${p.district.location.name ? p.district.location.name : '--'}`,
          districtStateId: `${p.district.stateLocationId ? p.district.stateLocationId : '--'}`,
          schoolCode: p.schoolCode,
          schoolName: `${p.school.location.name ? p.school.location.name : '--'}`,
          schoolStateId: `${p.school.stateLocationId ? p.school.stateLocationId : '--'}`,
          localId: `${p.localId || p.localStudentId || '--'}`,
          idInfo: p.sourceCode,
          lastUpdateDate: this.searchParams.selectedSearchInOption === 'Submission' && p.pidMatchTrans
            ? p.pidMatchTrans.lastUpdateDate
            : p.lastUpdateDate,
          retired: p.retired,
          ...(p.retiredReference && { associatedId: p.retiredReference.idMerged }),
          ...(this.searchParams.selectedSearchInOption === 'Submission' && { id: Number(p.id) }),
        }));
      }
      this.studentsObject = this.studentInfoCleaned;
      return this.studentsObject;
    },
    resetFilter() {
      if (this.districts.length) {
        this.$refs.selDistricts.clear();
      }
      if (this.schools.length) {
        this.$refs.selSchools.clear();
      }
      this.$refs.selPersonTypeFilter.clear();
      this.$refs.selGrades.clear();
      // this.studentsObject = this.students;
      // this.filterData();
    },
    traverseAndFlatten(currentNode, target, flattenedKey) {
      const localTarget = target;

      Object.keys(currentNode).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(currentNode, key)) {
          const newKey = flattenedKey === undefined ? key : `${flattenedKey}_${key}`;
          const value = currentNode[key];
          if (typeof value === 'object' && value !== null) {
            this.traverseAndFlatten(value, localTarget, newKey);
          } else {
            localTarget[newKey] = value;
          }
        }
      });
    },
    updateSorter({ field, order }) {
      this.columnHeaders = this.columnHeaders.map(header => ({
        ...header,
        order: header.field === field ? order : null,
      }));

      this.sortField = field;
      this.sortOrder = order;

      this.$router.replace({ query: this.filterSortParams });
    },
    setColumnSortOrder(field) {
      if (!this.filterSortParams.sortField && this.$route.query.sortField === field) {
        return this.$route.query.sortOrder;
      }
      if (this.filterSortParams.sortField === field) {
        return this.filterSortParams.sortOrder;
      }
      return null;
    },
    setDataElements() {
      this.userPersonTypesCodes = this.userPersonTypes
        .map(item => item.code);
      this.columnHeaders = [
        {
          header: null,
          field: null,
          show: this.isSearchByPerson,
        },
        {
          header: this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:STATE_STUDENT_ID`),
          field: 'personId',
          sortable: true,
          order: this.setColumnSortOrder('personId'),
          show: true,
        },
        {
          header: 'Name',
          field: 'name',
          sortable: true,
          order: this.setColumnSortOrder('name'),
          show: true,
        },
        {
          header: this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:DATE_OF_BIRTH`),
          field: 'dateOfBirth',
          sortable: true,
          order: this.setColumnSortOrder('dateOfBirth'),
          show: true,
        },
        {
          header: this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:GENDER_CODE`),
          field: 'genderCode',
          sortable: false,
          order: null,
          show: this.fieldEnabled('GENDER_CODE'),
        },
        {
          header: `${this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:ETHNICITY_INDICATOR`)} /
            ${this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:RACE_ETHNIC_CODE`)}`,
          field: 'raceEthnicCode',
          sortable: true,
          order: this.setColumnSortOrder('raceEthnicCode'),
          show: this.fieldEnabled('ETHNICITY_INDICATOR') && this.fieldEnabled('RACE_ETHNIC_CODE'),
          style: {
            width: '10%',
          },
        },
        {
          header: this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:ETHNICITY_INDICATOR`),
          field: 'raceEthnicCode',
          sortable: true,
          order: this.setColumnSortOrder('raceEthnicCode'),
          show: this.fieldEnabled('ETHNICITY_INDICATOR') && !this.fieldEnabled('RACE_ETHNIC_CODE'),
        },
        {
          header: this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:RACE_ETHNIC_CODE`),
          field: 'raceEthnicCode',
          sortable: true,
          order: this.setColumnSortOrder('raceEthnicCode'),
          show: this.fieldEnabled('RACE_ETHNIC_CODE') && !this.fieldEnabled('ETHNICITY_INDICATOR'),
        },
        {
          header: this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:SOCIAL_SECURITY_NUMBER`),
          field: 'socialSecurityNumber',
          sortable: false,
          order: null,
          show: this.fieldEnabled('SOCIAL_SECURITY_NUMBER'),
        },
        {
          header: this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:GRADE_CODE`),
          field: 'gradeCode',
          sortable: true,
          order: this.setColumnSortOrder('gradeCode'),
          show: this.fieldEnabled('GRADE_CODE'),
        },
        {
          header: 'Location Info',
          field: 'locationName',
          sortable: true,
          order: this.setColumnSortOrder('locationName'),
          show: this.fieldEnabled('DISTRICT_CODE') || this.fieldEnabled('SCHOOL_CODE'),
        },
        {
          header: `${this.getFieldLabel(`PER:${this.currentPersonTypeCode}:SEARCH:LOCAL_STUDENT_ID`)} Info`,
          field: 'sourceCode',
          sortable: false,
          order: null,
          show: true,
        },
        {
          header: 'Person Type',
          field: 'personType',
          sortable: false,
          order: null,
          show: this.isSearchByPerson && !this.contextMode,
        },
        {
          header: 'Match Score',
          field: 'matchPercent',
          sortable: !!(this.isSearchByPerson && this.searchParams.selectedMatchMethod === 'Matching Engine'),
          order: this.setColumnSortOrder('matchPercent'),
          show: !this.isSearchByAdvanced,
        },
        {
          header: 'Last Updated Date',
          field: 'lastUpdateDate',
          sortable: true,
          order: this.setColumnSortOrder('lastUpdateDate'),
          show: this.isSearchByAdvanced,
        },
        {
          header: 'Actions',
          field: 'actions',
          sortable: false,
          order: null,
          show: true,
        },
      ];
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return 'N/A';
    },
  },
};
</script>

<style lang="sass" scoped>
  table th
    max-width: 100%
    white-space: nowrap
  table td
    max-width: 100%
    white-space: wrap
  .disabled
    color: #999
    cursor:  default
    text-decoration:  none
    pointer-events: none
    &:hover
      text-decoration:  none
  .percent-label
    font-style: bold
    color: black
    font-size: 1.1rem
    font-weight: 800
    padding: .25rem 0 .25rem .15rem
  .button.is-primary:not(:last-child)
    margin-right: 1.5rem
  .actions-container
    display: flex
    align-items: center
    justify-content: flex-end
    height: 100%
  .table-wrapper
    overflow-x: auto
    white-space: nowrap
  .pager-container
    border-bottom: 1px solid #dee2e6
    border-width: 1px 0 0
    padding: 5.6px 12px
    background-color: #f9f9f9
</style>
